import { schemaError } from "../../schema/validate.js";

export interface SentryTracingOptions {
  enabled?: boolean;
  tracesSampleRate?: number;
  tracePropagationTargets?: Array<string | RegExp>;
}

export interface SentryReplayOptions {
  enabled?: boolean;
  replaysSessionSampleRate?: number;
  replaysOnErrorSampleRate?: number;
  maskAllText?: boolean;
  blockAllMedia?: boolean;
}

export interface SentryProfilingOptions {
  enabled?: boolean;
  profilesSampleRate?: number;
}

export interface SentryLogsOptions {
  enabled?: boolean;
}

/** React Router 7 — framework (`@sentry/react-router`) or library (`@sentry/react`) */
export interface SentryReactRouterOptions {
  enabled?: boolean;
  mode?: "framework" | "library";
  useInstrumentationAPI?: boolean;
}

export type SentryFeatureToggle<T extends { enabled?: boolean }> = boolean | T;

export interface SentryFeaturesConfig {
  tracing?: SentryFeatureToggle<SentryTracingOptions>;
  replay?: SentryFeatureToggle<SentryReplayOptions>;
  profiling?: SentryFeatureToggle<SentryProfilingOptions>;
  logs?: SentryFeatureToggle<SentryLogsOptions>;
  reactRouter?: SentryFeatureToggle<SentryReactRouterOptions>;
}

function parseSampleRate(value: unknown, field: string): number | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== "number" || value < 0 || value > 1) {
    throw schemaError(`${field} must be a number between 0 and 1`);
  }
  return value;
}

function parseBooleanOption(value: unknown, field: string): boolean | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== "boolean") {
    throw schemaError(`${field} must be a boolean`);
  }
  return value;
}

function parseStringList(value: unknown, field: string): string[] | undefined {
  if (value === undefined) return undefined;
  if (!Array.isArray(value)) {
    throw schemaError(`${field} must be an array`);
  }
  for (const item of value) {
    if (typeof item !== "string") {
      throw schemaError(`${field} items must be strings`);
    }
  }
  return value as string[];
}

function normalizeFeatureToggle<T extends { enabled?: boolean }>(
  value: unknown,
  parseObject: (raw: Record<string, unknown>) => T,
): T | undefined {
  if (value === undefined || value === false) return undefined;
  if (value === true) return { enabled: true } as T;
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw schemaError("feature must be a boolean or object");
  }
  return parseObject(value as Record<string, unknown>);
}

export function parseSentryFeatures(input: unknown): SentryFeaturesConfig | undefined {
  if (input === undefined) return undefined;
  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    throw schemaError("features must be an object");
  }

  const raw = input as Record<string, unknown>;

  const tracing = normalizeFeatureToggle(raw.tracing, (o) => ({
    enabled: parseBooleanOption(o.enabled, "tracing.enabled") ?? true,
    tracesSampleRate: parseSampleRate(o.tracesSampleRate, "tracing.tracesSampleRate"),
    tracePropagationTargets: parseStringList(
      o.tracePropagationTargets,
      "tracing.tracePropagationTargets",
    ),
  }));

  const replay = normalizeFeatureToggle(raw.replay, (o) => ({
    enabled: parseBooleanOption(o.enabled, "replay.enabled") ?? true,
    replaysSessionSampleRate: parseSampleRate(
      o.replaysSessionSampleRate,
      "replay.replaysSessionSampleRate",
    ),
    replaysOnErrorSampleRate: parseSampleRate(
      o.replaysOnErrorSampleRate,
      "replay.replaysOnErrorSampleRate",
    ),
    maskAllText: parseBooleanOption(o.maskAllText, "replay.maskAllText"),
    blockAllMedia: parseBooleanOption(o.blockAllMedia, "replay.blockAllMedia"),
  }));

  const profiling = normalizeFeatureToggle(raw.profiling, (o) => ({
    enabled: parseBooleanOption(o.enabled, "profiling.enabled") ?? true,
    profilesSampleRate: parseSampleRate(
      o.profilesSampleRate,
      "profiling.profilesSampleRate",
    ),
  }));

  const logs = normalizeFeatureToggle(raw.logs, (o) => ({
    enabled: parseBooleanOption(o.enabled, "logs.enabled") ?? true,
  }));

  const reactRouter = normalizeFeatureToggle(raw.reactRouter, (o) => {
    const mode = o.mode;
    if (mode !== undefined && mode !== "framework" && mode !== "library") {
      throw schemaError('reactRouter.mode must be "framework" or "library"');
    }
    return {
      enabled: parseBooleanOption(o.enabled, "reactRouter.enabled") ?? true,
      mode: (mode as SentryReactRouterOptions["mode"]) ?? "framework",
      useInstrumentationAPI:
        parseBooleanOption(
          o.useInstrumentationAPI,
          "reactRouter.useInstrumentationAPI",
        ) ?? true,
    };
  });

  const features: SentryFeaturesConfig = {};
  if (tracing?.enabled !== false) features.tracing = tracing;
  if (replay?.enabled !== false) features.replay = replay;
  if (profiling?.enabled !== false) features.profiling = profiling;
  if (logs?.enabled !== false) features.logs = logs;
  if (reactRouter?.enabled !== false) features.reactRouter = reactRouter;

  return Object.keys(features).length > 0 ? features : undefined;
}

export function isSentryFeatureEnabled(
  feature: SentryFeatureToggle<{ enabled?: boolean }> | undefined,
): feature is SentryFeatureToggle<{ enabled?: boolean }> {
  if (feature === undefined || feature === false) return false;
  if (feature === true) return true;
  return feature.enabled !== false;
}

export function resolveTracingOptions(
  feature: SentryFeaturesConfig["tracing"],
): SentryTracingOptions | undefined {
  if (!isSentryFeatureEnabled(feature)) return undefined;
  if (feature === true) return { enabled: true };
  return feature as SentryTracingOptions;
}

export function resolveReplayOptions(
  feature: SentryFeaturesConfig["replay"],
): SentryReplayOptions | undefined {
  if (!isSentryFeatureEnabled(feature)) return undefined;
  if (feature === true) return { enabled: true };
  return feature as SentryReplayOptions;
}

export function resolveProfilingOptions(
  feature: SentryFeaturesConfig["profiling"],
): SentryProfilingOptions | undefined {
  if (!isSentryFeatureEnabled(feature)) return undefined;
  if (feature === true) return { enabled: true };
  return feature as SentryProfilingOptions;
}

export function resolveLogsOptions(
  feature: SentryFeaturesConfig["logs"],
): SentryLogsOptions | undefined {
  if (!isSentryFeatureEnabled(feature)) return undefined;
  if (feature === true) return { enabled: true };
  return feature as SentryLogsOptions;
}

export function resolveReactRouterOptions(
  feature: SentryFeaturesConfig["reactRouter"],
): SentryReactRouterOptions | undefined {
  if (!isSentryFeatureEnabled(feature)) return undefined;
  if (feature === true) return { enabled: true, mode: "framework" };
  return feature as SentryReactRouterOptions;
}

/** Library mode: init Sentry explicitly via initClient().reactRouter.initLibrarySentry */
export function isSentryLibraryMode(
  features: SentryFeaturesConfig | undefined,
): boolean {
  const reactRouter = resolveReactRouterOptions(features?.reactRouter);
  return reactRouter?.mode === "library";
}
